import { skilledMigration } from '../data/siteData';
import { PageHero, CTASection } from '../components/UI';

export default function Migration({ setPage }) {
  return (
    <div>
      <PageHero
        breadcrumb="Migration & Immigration"
        title="Migrate with Confidence"
        subtitle="Whether you're a skilled professional, a student transitioning to permanent residency, or planning family migration — Travizon provides honest, expert guidance through every pathway."
      />

      {/* Pathways Grid */}
      <section className="py-24 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="section-tag" style={{ background: 'rgba(201,168,76,0.15)', color: '#E8C97A' }}>Migration Pathways</span>
            <h2 className="font-display text-4xl sm:text-5xl text-white font-semibold mt-1 mb-4">Explore Your Options</h2>
            <p className="text-white/55 text-lg max-w-xl mx-auto">Migration eligibility depends on your profile, qualifications, and country-specific rules. Our consultants provide honest assessments — no overpromising, ever.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {skilledMigration.map(p => (
              <div key={p.title} className="bg-white/[0.05] border border-white/10 rounded-2xl p-7 hover:bg-white/[0.08] hover:border-teal/40 transition-all duration-300">
                <div className="text-3xl mb-4">{p.icon}</div>
                <h4 className="font-body font-semibold text-white mb-2">{p.title}</h4>
                <p className="text-white/45 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="bg-gold/8 border border-gold/25 rounded-2xl p-6 flex gap-4 items-start">
            <span className="text-2xl flex-shrink-0 mt-0.5">⚠️</span>
            <p className="text-white/60 text-sm leading-relaxed">
              <strong className="text-white/80">Important Disclaimer: </strong>
              Immigration eligibility is determined solely by the immigration authorities of each country based on your individual profile, qualifications, and applicable regulations at the time of application. Travizon provides guidance and support but cannot guarantee visa or permanent residency approval. All information shared is for guidance purposes only and may change. A formal assessment by our consultants is required before applying.
            </p>
          </div>
        </div>
      </section>

      {/* Country Pathways */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="section-tag">Destination Pathways</span>
            <h2 className="font-display text-4xl text-navy font-semibold mt-1">Migration by Country</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { flag: '🇨🇦', country: 'Canada', visa: 'Express Entry', desc: 'Federal Skilled Worker Program and Provincial Nominee Programs for high-demand occupations.', points: ['Points-based CRS system', 'NOC occupation matching', 'PNP stream options', 'Family sponsorship'] },
              { flag: '🇦🇺', country: 'Australia', visa: 'SkillSelect', desc: "Points-tested pathways including the General Skilled Migration program and employer sponsorship.", points: ['Skills assessment required', 'State nomination available', 'Employer-sponsored stream', 'Regional visa options'] },
              { flag: '🇬🇧', country: 'United Kingdom', visa: 'Skilled Worker', desc: 'Employer-sponsored skilled worker visa for eligible occupations on the Shortage Occupation List.', points: ['Job offer required', 'Sponsor license check', 'Salary threshold', 'Settlement after 5 yrs'] },
              { flag: '🇳🇿', country: 'New Zealand', visa: 'Skilled Migrant', desc: 'Points-based category for skilled workers with job offers or employment in high-demand fields.', points: ['EOI submission', 'Skills assessment', 'Job offer bonus points', 'Accredited employer stream'] },
            ].map(({ flag, country, visa, desc, points }) => (
              <div key={country} className="bg-cream rounded-2xl p-7 hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300">
                <div className="text-4xl mb-4">{flag}</div>
                <h4 className="font-body font-semibold text-navy mb-1">{country}</h4>
                <span className="text-xs font-semibold text-teal bg-teal/10 px-2.5 py-1 rounded-full">{visa}</span>
                <p className="text-gray-500 text-sm leading-relaxed mt-3 mb-4">{desc}</p>
                <ul className="space-y-1.5">
                  {points.map(pt => (
                    <li key={pt} className="flex items-start gap-2 text-xs text-gray-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0 mt-1.5"></span>
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Professional Guidance */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="section-tag">Why Professional Help Matters</span>
              <h2 className="font-display text-4xl text-navy font-semibold mt-1 mb-5">Migration Is Too Important to Risk Getting Wrong</h2>
              <p className="text-gray-500 mb-6 leading-relaxed">A single missed document, incorrect form, or misunderstood rule can mean years of delay — or permanent ineligibility. Professional guidance isn't a luxury in migration. It's risk management.</p>
              <div className="space-y-4">
                {[
                  'Avoid costly refusals that can affect future applications',
                  'Ensure your skills assessment is done through the right authority',
                  'Understand exactly what points you can claim — and prove',
                  'Navigate state/provincial nomination with expert knowledge',
                  'Submit the strongest possible case on your first attempt',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="text-teal text-lg mt-0.5">✓</span>
                    <p className="text-gray-600 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-navy rounded-3xl p-10 text-white">
              <h3 className="font-display text-2xl mb-6">What Our Migration Consultation Covers</h3>
              <div className="space-y-5">
                {[
                  { n: '01', t: 'Profile Evaluation', d: 'We assess your qualifications, work experience, age, language scores, and adaptability factors.' },
                  { n: '02', t: 'Pathway Mapping', d: 'We identify every viable pathway available to you across all supported countries.' },
                  { n: '03', t: 'Realistic Timeline', d: 'We provide honest processing time estimates with current data — no guesswork.' },
                  { n: '04', t: 'Documentation Plan', d: 'A full checklist of what you need, and guidance on where to obtain each document.' },
                ].map(({ n, t, d }) => (
                  <div key={n} className="flex gap-4">
                    <div className="font-display text-2xl text-teal/50 font-light flex-shrink-0 w-8">{n}</div>
                    <div>
                      <h4 className="font-body font-semibold text-white text-sm mb-1">{t}</h4>
                      <p className="text-white/45 text-xs leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Is Migration Right for You?"
        subtitle="Book a free assessment with our migration consultants. We'll review your profile honestly and tell you exactly what pathways are realistic for you."
        setPage={setPage}
      />
    </div>
  );
}
